import React, { createElement, useEffect, useRef, type FC } from 'react';

import '@37signals/lexxy';

import '@37signals/lexxy/stylesheets/lexxy.css';

type LexxyEditorChangeEvent = React.ChangeEvent<LexxyEditorElement>;

interface LexxyEditorHTMLElementEventMap extends HTMLElementEventMap {
  'lexxy:change': LexxyEditorChangeEvent;
}

type LexxyEditorElement = HTMLElement & {
  value: string;

  addEventListener<K extends keyof LexxyEditorHTMLElementEventMap>(
    type: K,
    listener: (event: LexxyEditorHTMLElementEventMap[K]) => void,
  ): void;

  removeEventListener<K extends keyof LexxyEditorHTMLElementEventMap>(
    type: K,
    listener: (event: LexxyEditorHTMLElementEventMap[K]) => void,
  ): void;
};

export type LexxyEditorProps = {
  id?: string;
  name?: string;
  value: string;
  onChange(value: string): void;
};

export const LexxyEditor: FC<LexxyEditorProps> = ({ id, name, value, onChange }) => {
  const ref = useRef<LexxyEditorElement>(null);
  const onChangeRef = useRef<LexxyEditorProps['onChange']>(onChange);

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  useEffect(() => {
    const editor = ref.current;
    if (!editor) return;

    const onInput = () => {
      onChangeRef.current?.(editor.value);
    };

    editor.addEventListener('lexxy:change', onInput);

    return () => {
      editor.removeEventListener('lexxy:change', onInput);
    };
  }, []);

  useEffect(() => {
    const editor = ref.current;
    if (!editor) return;

    if (editor.value !== value) editor.value = value;
  }, [value]);

  return createElement('lexxy-editor', {
    ref,
    id,
    name,
    'data-direct-upload-url': '/rails/active_storage/direct_uploads',
    'data-blob-url-template': '/rails/active_storage/blobs/redirect/:signed_id/:filename',
  });
};
